import { call, put } from 'redux-saga/effects';

import { logout as swLogout, clearAuthRecord, resetSilentLoginGuard } from '../../auth/authClient';
import { clear as clearLogin } from '../../redux/login/actions';
import { clear as clearFreeMinutes } from '../../redux/freeminutes/actions';
import { clear as clearUser } from '../../redux/user/actions';
import { clearInvoicesReplay } from '../../redux/invoicesReplay/actions';
import { resetStripe } from '../../redux/app/actions';
import { messagesShow } from '../../redux/messages/actions';

export default function*() {
    // The Service Worker clears the stored session and does the best-effort IDP
    // logout for the active mode (ROPC logout endpoint / Microsoft logout URL
    // persisted at login / OAuth2 end-session endpoint). We drive the final
    // navigation ourselves, so pass redirect: false — for OAuth2 that means the
    // SW hands the end-session URL back instead of navigating to it, and we must
    // follow it below or the IDP SSO cookie survives the logout.
    //
    // The SW signals failure by RESOLVING {ok:false}, never by throwing, so the
    // result has to be inspected: the catch below only covers a synchronous
    // requireClient() throw.
    let swResult = null;
    try {
        swResult = yield call(swLogout, { redirect: false });
    } catch (error) {
        console.error('logout: Service Worker logout threw', error);
    }

    const swCleared = Boolean(swResult && swResult.ok);
    if (!swCleared) {
        console.error(
            'logout: the Service Worker did not confirm the session clear',
            swResult && swResult.code,
            swResult && swResult.message
        );
        // The worker's IndexedDB record — not Redux — is what the next boot
        // reads. Tearing down only Redux would leave a live session that the
        // next page load silently restores, handing the account to whoever uses
        // the device next.
        try {
            yield call(clearAuthRecord);
        } catch (error) {
            console.error('logout: the local session record could not be cleared either', error);
            // TODO(localise): a dedicated `logoutIncomplete` key ("close all tabs")
            // would be actionable; unknown keys render as their own name.
            yield put(messagesShow('internalServerError', 'error'));
        }
    }

    // The guard bounds a failing prompt=none; it must not outlive the session
    // it was set for, or re-entering through the bridge in this tab is a no-op.
    yield call(resetSilentLoginGuard);

    yield put(clearLogin());
    yield put(clearFreeMinutes());
    yield put(clearUser());
    yield put(clearInvoicesReplay());
    yield put(resetStripe());

    // For OAuth2 the SW returns the IDP end-session URL instead of navigating to
    // it (redirect: false). Following it is what actually ends the SSO session —
    // otherwise "Continue with SSO" silently signs the user straight back in.
    const endSessionUrl = swResult && swResult.ok && swResult.data && swResult.data.endSessionUrl;
    window.location.href = endSessionUrl || '/login';
}
